import { 
  Megaphone, 
  TrendingUp, 
  Users, 
  Mail, 
  Share2, 
  BarChart3, 
  Target, 
  Zap,
  Video,
  Link,
  MessageCircle,
  Database,
  Award,
  Mic,
  Smartphone,
  Activity,
  Send,
  Globe,
  Play
} from 'lucide-react';
import type { Product } from './types';

export const marketingProducts: Product[] = [
  {
    id: 'digital-marketing-automation',
    title: 'Digital Marketing Automation',
    subtitle: 'Complete marketing automation platform',
    description: 'Comprehensive marketing automation with email campaigns, lead nurturing, analytics, and multi-channel orchestration.',
    icon: Megaphone,
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Marketing & Sales',
    status: 'Available',
    businessTypes: ['#marketing-agencies', '#enterprise', '#sme', '#ecommerce', '#digital-marketing'],
    features: [
      'Email Campaign Automation',
      'Lead Scoring & Nurturing',
      'Multi-Channel Campaign Management',
      'Customer Journey Mapping',
      'Marketing Analytics & ROI Tracking',
      'Landing Page Builder',
      'Social Media Integration',
      'CRM Integration & Lead Management'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-blue-500 to-indigo-600'
  },
  {
    id: 'social-media-management',
    title: 'Social Media Management Suite',
    subtitle: 'Complete social media platform',
    description: 'Comprehensive social media management with content scheduling, analytics, engagement tracking, and team collaboration.',
    icon: Share2,
    image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Marketing & Sales',
    status: 'Available',
    businessTypes: ['#social-media', '#marketing-agencies', '#brands', '#influencers', '#content-creators'],
    features: [
      'Multi-Platform Content Scheduling',
      'Social Media Analytics & Insights',
      'Engagement Management & Response',
      'Content Library & Asset Management',
      'Team Collaboration & Approval Workflows',
      'Hashtag Research & Optimisation with UK market trends',
      'Competitor Analysis & Benchmarking',
      'Influencer Management & Outreach'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-pink-500 to-purple-600'
  },
  {
    id: 'marketing-analytics-dashboard',
    title: 'Marketing Analytics Dashboard',
    subtitle: 'Advanced marketing intelligence platform',
    description: 'Comprehensive marketing analytics with performance tracking, attribution modeling, and predictive insights.',
    icon: BarChart3,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Marketing & Sales',
    status: 'Available',
    businessTypes: ['#marketing-analytics', '#enterprise', '#agencies', '#performance-marketers', '#data-analysts'],
    features: [
      'Multi-Channel Attribution Modeling',
      'Customer Acquisition Cost (CAC) Analysis',
      'Marketing ROI & Performance Tracking',
      'Conversion Funnel Analysis',
      'A/B Testing & Experimentation',
      'Predictive Marketing Analytics',
      'Custom Dashboard Creation',
      'Integration with Marketing Platforms'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-green-500 to-teal-600'
  },
  {
    id: 'content-marketing-platform',
    title: 'Content Marketing Platform',
    subtitle: 'Complete content strategy solution',
    description: 'End-to-end content marketing platform with planning, creation, distribution, and performance tracking.',
    icon: Target,
    image: 'https://images.unsplash.com/photo-1542744094-3a31f272c490?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Marketing & Sales',
    status: 'Available',
    businessTypes: ['#content-marketing', '#marketing-agencies', '#publishers', '#brands', '#seo-agencies'],
    features: [
      'Content Strategy & Planning',
      'Editorial Calendar & Workflow',
      'Content Creation & Collaboration',
      'SEO Optimisation & Keyword Research for UK market',
      'Content Distribution & Publishing',
      'Performance Analytics & Insights',
      'Content Library & Asset Management',
      'Team Collaboration & Review Process'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-orange-500 to-red-600'
  },
  {
    id: 'invideo-platform',
    title: 'InVideo',
    subtitle: 'Professional video creation platform',
    description: 'Video creation platform with ready-made templates and extensive media library, serving over 7 million users worldwide.',
    icon: Video,
    image: 'https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Marketing & Sales',
    status: 'Available',
    businessTypes: ['#video-marketing', '#content-creators', '#marketing-agencies', '#social-media', '#video-production'],
    features: [
      'Professional Video Templates',
      'Extensive Media Library',
      'Text-to-Video AI Technology',
      'Brand Kit & Custom Templates',
      'Multi-Language Support',
      'Collaboration & Team Features',
      'Social Media Optimization',
      'Advanced Video Editing Tools',
      'Voice-Over & Music Integration',
      'HD Video Export & Publishing'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-red-500 to-pink-600'
  },
  {
    id: 'linktree-platform',
    title: 'Linktree',
    subtitle: 'Social media link aggregation',
    description: 'Link-in-bio solution used by over 50 million users to showcase their most important content across social networks.',
    icon: Link,
    image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Marketing & Sales',
    status: 'Available',
    businessTypes: ['#social-media', '#influencers', '#creators', '#brands', '#link-management'],
    features: [
      'Customizable Landing Pages',
      'Advanced Analytics & Insights',
      'QR Code Generation',
      'Custom Branding & Themes',
      'Link Scheduling & Management',
      'E-commerce Integration',
      'Social Media Integration',
      'Mobile-Optimized Design',
      'Contact Form Integration',
      'Pro Analytics Dashboard'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-green-500 to-teal-600'
  },
  {
    id: 'personalized-video-marketing',
    title: 'Personalized Video Marketing',
    subtitle: 'AI-powered video personalization',
    description: 'Creates customized video content at scale for individual customers based on their preferences and behaviors.',
    icon: Play,
    image: 'https://images.unsplash.com/photo-1598300042247-d088f8ab3a91?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Marketing & Sales',
    status: 'Available',
    businessTypes: ['#video-marketing', '#personalization', '#ai-marketing', '#customer-engagement', '#automation'],
    features: [
      'AI-Powered Video Personalization',
      'Dynamic Content Generation',
      'Customer Data Integration',
      'A/B Testing & Optimisation with UK audience insights',
      'Multi-Channel Distribution',
      'Performance Analytics',
      'Template Library & Customization',
      'Real-Time Video Rendering',
      'CRM Integration',
      'Automated Campaign Management'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-purple-500 to-indigo-600'
  },
  {
    id: 'account-based-marketing',
    title: 'Account-Based Marketing Platform',
    subtitle: 'Targeted B2B marketing automation',
    description: 'Orchestrates targeted marketing campaigns to specific high-value accounts with personalized messaging and content.',
    icon: Target,
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Marketing & Sales',
    status: 'Available',
    businessTypes: ['#b2b-marketing', '#account-based-marketing', '#enterprise', '#lead-generation', '#personalization'],
    features: [
      'Account Identification & Targeting',
      'Multi-Channel Campaign Orchestration',
      'Personalized Content Delivery',
      'Intent Data Integration',
      'Sales & Marketing Alignment',
      'Account Scoring & Prioritization',
      'Advanced Analytics & ROI Tracking',
      'CRM Integration & Sync',
      'Custom Audience Segmentation',
      'Campaign Performance Optimization'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-blue-500 to-cyan-600'
  },
  {
    id: 'conversational-marketing',
    title: 'Conversational Marketing Platform',
    subtitle: 'Real-time visitor engagement',
    description: 'Engages website visitors in real-time conversations to qualify leads and book meetings automatically.',
    icon: MessageCircle,
    image: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Marketing & Sales',
    status: 'Available',
    businessTypes: ['#conversational-marketing', '#lead-generation', '#chatbots', '#customer-engagement', '#automation'],
    features: [
      'AI-Powered Chatbots',
      'Real-Time Visitor Tracking',
      'Lead Qualification Automation',
      'Meeting Scheduling Integration',
      'Multi-Channel Messaging',
      'Conversation Analytics',
      'Custom Playbooks & Workflows',
      'CRM Integration & Lead Sync',
      'A/B Testing & Optimisation with UK compliance (ASA standards)',
      'Mobile-Responsive Chat Widget'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-teal-500 to-green-600'
  },
  {
    id: 'customer-data-platform',
    title: 'Customer Data Platform',
    subtitle: 'Unified customer intelligence',
    description: 'Unifies customer data from multiple sources to create comprehensive profiles for targeted marketing campaigns.',
    icon: Database,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Marketing & Sales',
    status: 'Available',
    businessTypes: ['#customer-data', '#data-unification', '#personalization', '#marketing-automation', '#analytics'],
    features: [
      'Multi-Source Data Integration',
      'Real-Time Customer Profiles',
      'Advanced Segmentation Tools',
      'Identity Resolution & Matching',
      'Privacy & Compliance Management',
      'API & Integration Capabilities',
      'Customer Journey Mapping',
      'Predictive Analytics',
      'Data Quality & Governance',
      'Cross-Channel Activation'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-indigo-500 to-purple-600'
  },
  {
    id: 'sales-enablement-platform',
    title: 'Sales Enablement Platform',
    subtitle: 'Sales team productivity suite',
    description: 'Provides sales teams with content, tools, and insights to improve effectiveness throughout the sales cycle.',
    icon: Award,
    image: 'https://images.unsplash.com/photo-1556155092-8707de31f9c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Marketing & Sales',
    status: 'Available',
    businessTypes: ['#sales-enablement', '#sales-tools', '#content-management', '#sales-training', '#productivity'],
    features: [
      'Sales Content Management',
      'Pitch Deck & Presentation Tools',
      'Sales Training & Coaching',
      'Performance Analytics & Insights',
      'CRM Integration & Data Sync',
      'Competitive Intelligence',
      'Sales Playbook Management',
      'Email Templates & Sequences',
      'Meeting & Demo Scheduling',
      'ROI Tracking & Reporting'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-yellow-500 to-orange-600'
  },
  {
    id: 'voice-marketing-analytics',
    title: 'Voice Marketing Analytics',
    subtitle: 'Call intelligence platform',
    description: 'Analyses customer service calls to extract insights for improving marketing messaging and identifying upsell opportunities with UK advertising standards compliance.',
    icon: Mic,
    image: 'https://images.unsplash.com/photo-1589254065878-42c9da997008?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Marketing & Sales',
    status: 'Available',
    businessTypes: ['#voice-analytics', '#call-intelligence', '#customer-insights', '#marketing-optimization', '#ai-analytics'],
    features: [
      'Real-Time Call Transcription',
      'Sentiment Analysis & Emotion Detection',
      'Keyword & Topic Identification',
      'Customer Journey Mapping',
      'Upsell Opportunity Detection',
      'Agent Performance Analytics',
      'Compliance & Quality Monitoring',
      'Integration with CRM Systems',
      'Custom Reporting Dashboards',
      'AI-Powered Insights & Recommendations'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-violet-500 to-purple-600'
  },
  {
    id: 'social-media-advertising',
    title: 'Social Media Advertising Optimization',
    subtitle: 'AI-powered ad optimisation',
    description: 'Uses AI to automatically optimise social media ad campaigns for maximum ROI across multiple platforms with ASA compliance for UK advertising standards.',
    icon: Smartphone,
    image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Marketing & Sales',
    status: 'Available',
    businessTypes: ['#social-advertising', '#ai-optimisation', '#paid-media', '#performance-marketing', '#roi-optimisation'],
    features: [
      'Cross-Platform Campaign Management',
      'AI-Powered Bid Optimisation',
      'Audience Targeting & Lookalikes',
      'Creative Testing & Optimisation with UK market preferences',
      'Real-Time Performance Monitoring',
      'Budget Allocation & Pacing',
      'Fraud Detection & Prevention',
      'Attribution & Conversion Tracking',
      'Automated Reporting & Insights',
      'Integration with Major Ad Platforms'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-pink-500 to-red-600'
  },
  {
    id: 'marketing-attribution',
    title: 'Marketing Attribution Platform',
    subtitle: 'Multi-touch attribution analytics',
    description: 'Measures the impact of marketing efforts across channels to determine which initiatives drive conversions.',
    icon: Activity,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Marketing & Sales',
    status: 'Available',
    businessTypes: ['#marketing-attribution', '#analytics', '#performance-measurement', '#roi-analysis', '#data-driven-marketing'],
    features: [
      'Multi-Touch Attribution Modeling',
      'Cross-Channel Journey Tracking',
      'Revenue Attribution & ROI Analysis',
      'Customer Lifetime Value Calculation',
      'Marketing Mix Modeling',
      'Real-Time Performance Dashboards',
      'Custom Attribution Models',
      'Data Integration & API Connectivity',
      'Conversion Path Analysis',
      'Budget Optimization Recommendations'
    ],
    pricing: { setup: 32000, monthly: 1472 },
    color: 'from-emerald-500 to-teal-600'
  },
  {
    id: 'email-marketing-automation',
    title: 'Email Marketing Automation',
    subtitle: 'Advanced email campaign platform',
    description: 'Comprehensive email marketing platform with automation, personalization, and advanced analytics.',
    icon: Mail,
    image: 'https://images.unsplash.com/photo-1596526131083-e8c633c948d2?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Marketing & Sales',
    status: 'Available',
    businessTypes: ['#email-marketing', '#automation', '#personalization', '#drip-campaigns', '#newsletter-marketing'],
    features: [
      'Drag & Drop Email Builder',
      'Advanced Segmentation & Targeting',
      'Automated Drip Campaigns',
      'A/B Testing & Optimisation with UK email regulations',
      'Personalization & Dynamic Content',
      'Deliverability Optimisation for UK ISP requirements',
      'Advanced Analytics & Reporting',
      'Integration with E-commerce Platforms',
      'SMS Marketing Integration',
      'GDPR Compliance & List Management'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-blue-500 to-indigo-600'
  },
  {
    id: 'influencer-marketing-platform',
    title: 'Influencer Marketing Platform',
    subtitle: 'Creator collaboration management',
    description: 'End-to-end influencer marketing platform for discovering, managing, and measuring creator partnerships.',
    icon: Users,
    image: 'https://images.unsplash.com/photo-1611162616475-46b635cb6868?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Marketing & Sales',
    status: 'Available',
    businessTypes: ['#influencer-marketing', '#creator-economy', '#brand-partnerships', '#social-media', '#content-marketing'],
    features: [
      'Influencer Discovery & Search',
      'Campaign Management & Workflow',
      'Performance Tracking & Analytics',
      'Payment & Contract Management',
      'Content Approval & Collaboration',
      'Fraud Detection & Verification',
      'ROI Measurement & Attribution',
      'Relationship Management CRM',
      'Competitor Analysis',
      'Compliance & FTC Guidelines'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-rose-500 to-pink-600'
  },
  {
    id: 'mql-scoring-platform',
    title: 'Marketing Qualified Lead Scoring',
    subtitle: 'AI-powered lead qualification',
    description: 'Uses machine learning to score and qualify marketing leads based on behavior, demographics, and engagement.',
    icon: Target,
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Marketing & Sales',
    status: 'Available',
    businessTypes: ['#lead-scoring', '#lead-qualification', '#ai-marketing', '#sales-enablement', '#marketing-automation'],
    features: [
      'Machine Learning Lead Scoring',
      'Behavioral Tracking & Analysis',
      'Demographic & Firmographic Data',
      'Real-Time Lead Qualification',
      'CRM Integration & Sync',
      'Custom Scoring Models',
      'Lead Nurturing Automation',
      'Sales Alert & Notifications',
      'Performance Analytics & Reporting',
      'A/B Testing & Model Optimization'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-cyan-500 to-blue-600'
  },
  {
    id: 'cross-channel-campaign',
    title: 'Cross-Channel Campaign Management',
    subtitle: 'Unified marketing orchestration',
    description: 'Orchestrates marketing campaigns across multiple channels with consistent messaging and automated optimization.',
    icon: Globe,
    image: 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Marketing & Sales',
    status: 'Available',
    businessTypes: ['#cross-channel-marketing', '#campaign-management', '#marketing-automation', '#omnichannel', '#integrated-marketing'],
    features: [
      'Multi-Channel Campaign Orchestration',
      'Unified Customer Journey Mapping',
      'Cross-Channel Message Consistency',
      'Automated Campaign Optimization',
      'Real-Time Performance Monitoring',
      'Channel Attribution & Analysis',
      'Audience Segmentation & Targeting',
      'Creative Asset Management',
      'Campaign ROI Measurement',
      'Integration with Major Platforms'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-purple-500 to-pink-600'
  },
  {
    id: 'marketing-performance-dashboard',
    title: 'Marketing Performance Dashboard',
    subtitle: 'Real-time marketing insights',
    description: 'Comprehensive dashboard providing real-time insights into marketing performance across all channels and campaigns.',
    icon: BarChart3,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Marketing & Sales',
    status: 'Available',
    businessTypes: ['#marketing-dashboards', '#performance-analytics', '#real-time-insights', '#marketing-intelligence', '#data-visualization'],
    features: [
      'Real-Time Performance Monitoring',
      'Custom Dashboard Creation',
      'Multi-Channel Data Integration',
      'Advanced Visualization Tools',
      'Automated Reporting & Alerts',
      'Predictive Analytics & Forecasting',
      'Team Collaboration & Sharing',
      'Mobile-Responsive Design',
      'Data Export & API Access',
      'Performance Benchmarking'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-green-500 to-emerald-600'
  }
];